"use client"

import { Field, FieldGroup, FieldLabel } from "@/components/ui/field"
import { Label } from "@/components/ui/label"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { TabsContent } from "@/components/ui/tabs"

import { IMAGE_SKILL_CONFIGS, ImageSkill } from "./config"
import { SliderWithStops } from "./slider-with-stops"

type ImagePanelProps = {
  skill: ImageSkill
  onSkillChange: (skill: ImageSkill) => void
  complexityIndex: number
  onComplexityIndexChange: (index: number) => void
}

export function ImagePanel({
  skill,
  onSkillChange,
  complexityIndex,
  onComplexityIndexChange,
}: ImagePanelProps) {
  const skillConfig = IMAGE_SKILL_CONFIGS[skill] ?? IMAGE_SKILL_CONFIGS.query
  const sliderValues = skillConfig.stops.map((stop) => stop.tokens)
  const safeIndex = Math.min(Math.max(complexityIndex, 0), Math.max(sliderValues.length - 1, 0))
  const formatStopLabel = (value: number) => {
    const stop = skillConfig.stops.find((item) => item.tokens === value)
    return stop ? `${stop.label} (${stop.tokens} tokens)` : `${value} tokens`
  }

  return (
    <TabsContent value="image">
      <Card>
        <CardHeader>
          <CardTitle>Image Processing</CardTitle>
          <CardDescription>
            Pick a vision skill and tune the response complexity to see per-image pricing.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <FieldGroup>
            <Field>
              <FieldLabel>Skill</FieldLabel>
              <RadioGroup
                value={skill}
                onValueChange={(next) => onSkillChange(next as ImageSkill)}
                className="grid gap-3 sm:grid-cols-2"
              >
                {(Object.keys(IMAGE_SKILL_CONFIGS) as ImageSkill[]).map((option) => {
                  const optionConfig = IMAGE_SKILL_CONFIGS[option]
                  const optionId = `image-skill-${option.replace(/\s+/g, "-")}`
                  return (
                    <div key={option} className="flex items-center gap-3 rounded-md border p-3">
                      <RadioGroupItem value={option} id={optionId} />
                      <Label htmlFor={optionId}>{optionConfig.label}</Label>
                    </div>
                  )
                })}
              </RadioGroup>
            </Field>
            <SliderWithStops
              id={`image-complexity-${skill}`}
              label={skillConfig.sliderLabel}
              values={sliderValues}
              valueIndex={safeIndex}
              onIndexChange={onComplexityIndexChange}
              formatStopLabel={formatStopLabel}
              fullWidth
            />
          </FieldGroup>
        </CardContent>
      </Card>
    </TabsContent>
  )
}
